import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import StatsCard from '../components/StatsCard';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiUsers, FiFileText, FiMail, FiPlay, FiRefreshCw, FiClock, 
  FiCheckCircle, FiXCircle, FiAlertCircle, FiDatabase, FiSettings 
} = FiIcons;

const AdminDashboard = () => {
  const [stats, setStats] = useState({});
  const [ingestionLogs, setIngestionLogs] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, ingestionRes, emailRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/ingestion-logs?limit=10'),
        api.get('/admin/email-logs?limit=10')
      ]);

      setStats(statsRes.data);
      setIngestionLogs(ingestionRes.data.logs);
      setEmailLogs(emailRes.data.logs);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      setError('Error al cargar los datos administrativos');
    } finally {
      setLoading(false);
    }
  };

  const handleManualIngestion = async () => {
    setActionLoading(prev => ({ ...prev, ingestion: true }));
    setError('');
    setSuccess('');

    try {
      await api.post('/admin/ingest');
      setSuccess('Ingesta manual completada exitosamente');
      fetchData(); // Refresh data
    } catch (error) {
      setError('Error en la ingesta manual: ' + (error.response?.data?.details || error.message));
    } finally {
      setActionLoading(prev => ({ ...prev, ingestion: false }));
    }
  };

  const handleSendDailyEmails = async () => {
    setActionLoading(prev => ({ ...prev, daily: true }));
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/admin/send-daily-emails');
      setSuccess(`Emails diarios enviados: ${response.data.result.sent} exitosos, ${response.data.result.failed} fallidos`);
      fetchData(); // Refresh data
    } catch (error) {
      setError('Error enviando emails diarios: ' + (error.response?.data?.details || error.message));
    } finally {
      setActionLoading(prev => ({ ...prev, daily: false }));
    }
  };

  const handleSendWeeklyEmails = async () => {
    setActionLoading(prev => ({ ...prev, weekly: true }));
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/admin/send-weekly-emails');
      setSuccess(`Emails semanales enviados: ${response.data.result.sent} exitosos, ${response.data.result.failed} fallidos`);
      fetchData(); // Refresh data
    } catch (error) {
      setError('Error enviando emails semanales: ' + (error.response?.data?.details || error.message));
    } finally {
      setActionLoading(prev => ({ ...prev, weekly: false }));
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
      case 'sent':
        return FiCheckCircle;
      case 'error':
      case 'failed':
        return FiXCircle;
      default:
        return FiAlertCircle;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
      case 'sent':
        return 'text-green-600';
      case 'error':
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-yellow-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Panel de Administración</h1>
              <p className="text-gray-600 mt-1">
                Gestión del sistema y monitoreo de procesos
              </p>
            </div>
            
            <button
              onClick={fetchData}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
              <span>Actualizar</span>
            </button>
          </div>

          {/* Alerts */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md mb-6">
              {success}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
              {error}
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatsCard
              title="Usuarios totales"
              value={stats.users?.total_users || 0}
              icon={FiUsers}
              color="blue"
              description={`${stats.users?.pro_users || 0} Pro, ${stats.users?.free_users || 0} Free`}
            />
            
            <StatsCard
              title="Items BOE"
              value={stats.items?.total_items || 0}
              icon={FiFileText}
              color="green"
              description={`${stats.items?.today_items || 0} hoy, ${stats.items?.week_items || 0} esta semana`}
            />
            
            <StatsCard
              title="Última ingesta"
              value={stats.lastIngestion?.status === 'success' ? 'Éxito' : 'Error'}
              icon={FiDatabase}
              color={stats.lastIngestion?.status === 'success' ? 'green' : 'red'}
              description={stats.lastIngestion ? 
                `${stats.lastIngestion.items_guardados || 0} items guardados` : 
                'No disponible'
              }
            />
            
            <StatsCard
              title="Usuarios activos"
              value={stats.users?.active_users || 0}
              icon={FiUsers}
              color="purple"
              description="Cuentas activas"
            />
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Acciones manuales
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <button
                onClick={handleManualIngestion}
                disabled={actionLoading.ingestion}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <SafeIcon 
                  icon={actionLoading.ingestion ? FiRefreshCw : FiPlay} 
                  className={`w-4 h-4 ${actionLoading.ingestion ? 'animate-spin' : ''}`} 
                />
                <span>Ingesta manual</span>
              </button>
              
              <button
                onClick={handleSendDailyEmails}
                disabled={actionLoading.daily}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                <SafeIcon 
                  icon={actionLoading.daily ? FiRefreshCw : FiMail} 
                  className={`w-4 h-4 ${actionLoading.daily ? 'animate-spin' : ''}`} 
                />
                <span>Emails diarios</span>
              </button>
              
              <button
                onClick={handleSendWeeklyEmails}
                disabled={actionLoading.weekly}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                <SafeIcon 
                  icon={actionLoading.weekly ? FiRefreshCw : FiMail} 
                  className={`w-4 h-4 ${actionLoading.weekly ? 'animate-spin' : ''}`} 
                />
                <span>Emails semanales</span>
              </button>
            </div>
          </div>

          {/* Logs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Ingestion Logs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <SafeIcon icon={FiDatabase} className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Logs de ingesta
                </h2>
              </div>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {ingestionLogs.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No hay logs disponibles</p>
                ) : (
                  ingestionLogs.map((log, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <div className="flex items-center space-x-3">
                        <SafeIcon 
                          icon={getStatusIcon(log.status)} 
                          className={`w-5 h-5 ${getStatusColor(log.status)}`} 
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {format(new Date(log.fecha_ingesta), "d 'de' MMMM", { locale: es })}
                          </div>
                          <div className="text-xs text-gray-500">
                            {log.items_guardados || 0} items guardados
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        <SafeIcon icon={FiClock} className="w-3 h-3 inline mr-1" />
                        {log.execution_time_ms ? `${log.execution_time_ms}ms` : 'N/A'}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Email Logs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <SafeIcon icon={FiMail} className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Logs de emails
                </h2>
              </div>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {emailLogs.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No hay logs disponibles</p>
                ) : (
                  emailLogs.map((log, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <div className="flex items-center space-x-3">
                        <SafeIcon 
                          icon={getStatusIcon(log.status)} 
                          className={`w-5 h-5 ${getStatusColor(log.status)}`} 
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {log.user_email || 'Usuario eliminado'}
                          </div>
                          <div className="text-xs text-gray-500 capitalize">
                            {log.tipo} - {format(new Date(log.fecha_envio), "d/M HH:mm", { locale: es })}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 capitalize">
                        {log.status}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;